import { useCallback, useEffect, useRef, useState } from 'react';

import crmOpportunityService from '../../domain/services/crm/crm.opportunity.service';
import loginService from '../../domain/services/login.service';
import {
    conversationRefusalReasons,
    conversationSlowThresholdMs,
    conversationTexts,
} from '../../domain/constant/crm/crm.conversation.constant';

export const conversationStatuses = {
    loading: 'loading',
    ready: 'ready',
    unavailable: 'unavailable',
    error: 'error',
    sellerChanged: 'sellerChanged',
};

const sellerChangeReasons = Object.values(conversationRefusalReasons);

export const describeOpenFailure = (error = {}, fallbackMessage = conversationTexts.openFailed) => {
    const body = error.response?.data || {};

    if (sellerChangeReasons.includes(body.reason))
        return { status: conversationStatuses.sellerChanged, message: body.message };

    if (body.businessError && body.message)
        return { status: conversationStatuses.error, message: body.message };

    if (error.code === 'ECONNABORTED')
        return { status: conversationStatuses.error, message: conversationTexts.timeout };

    return { status: conversationStatuses.error, message: fallbackMessage };
};

const embedSource = 'orbita-agent-chat';
const gesttoSource = 'facilita-gestto-chat';

const getOrigin = (url) => {
    try {
        return new URL(url).origin;
    } catch (error) {
        return null;
    }
};

const useOrbitaConversation = (opportunityId, { isOpen = true, onSent, onReleased, activityId = null } = {}) => {

    const [status, setStatus] = useState(conversationStatuses.loading);
    const [conversation, setConversation] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [hasDraft, setHasDraft] = useState(false);
    const [sessionAttempt, setSessionAttempt] = useState(0);
    const [isSlow, setIsSlow] = useState(false);

    const frameRef = useRef(null);
    const isMountedRef = useRef(true);
    const shownOpportunityRef = useRef(null);
    const onSentRef = useRef(onSent);
    const onReleasedRef = useRef(onReleased);
    const hasNotifiedSentRef = useRef(false);

    useEffect(() => () => { isMountedRef.current = false; }, []);

    useEffect(() => { onSentRef.current = onSent; }, [onSent]);

    useEffect(() => { onReleasedRef.current = onReleased; }, [onReleased]);

    const load = useCallback(async () => {
        if (!opportunityId)
            return;

        shownOpportunityRef.current = opportunityId;
        setStatus(conversationStatuses.loading);
        setErrorMessage(null);
        setIsSlow(false);

        const isCurrent = () => isMountedRef.current && shownOpportunityRef.current === opportunityId;

        try {
            const loaded = await crmOpportunityService.findConversation(opportunityId);

            if (!isCurrent())
                return;

            setConversation(loaded);

            if (!loaded?.embedUrl)
                setStatus(conversationStatuses.unavailable);
        } catch (error) {
            if (!isCurrent())
                return;

            const failure = describeOpenFailure(error);
            setErrorMessage(failure.message);
            setStatus(failure.status);
        }
    }, [opportunityId]);

    useEffect(() => {
        if (isOpen)
            load();
    }, [isOpen, load]);

    useEffect(() => {
        if (!isOpen || status !== conversationStatuses.loading)
            return undefined;

        const slowTimer = setTimeout(() => setIsSlow(true), conversationSlowThresholdMs);

        return () => clearTimeout(slowTimer);
    }, [isOpen, status, opportunityId]);

    const reload = useCallback(async () => {
        await load();
        setSessionAttempt(attempt => attempt + 1);
    }, [load]);

    const ticketId = conversation?.ticketId || null;
    const embedUrl = conversation?.embedUrl || null;
    const embedOrigin = embedUrl ? getOrigin(embedUrl) : null;

    const embedSrc = embedUrl
        ? `${embedUrl}?parentOrigin=${encodeURIComponent(window.location.origin)}`
        : null;

    const registerSentMessage = useCallback(async (payload = {}) => {
        const registered = await crmOpportunityService
            .registerSentMessage(opportunityId, { messageId: payload.messageId, body: payload.body, activityId })
            .catch(() => null);

        if (!isMountedRef.current || hasNotifiedSentRef.current)
            return;

        hasNotifiedSentRef.current = true;

        if (onSentRef.current)
            onSentRef.current(registered?.activity || {});
    }, [opportunityId, activityId]);

    useEffect(() => {
        if (!embedOrigin || !ticketId)
            return undefined;

        const sendSession = () => frameRef.current?.contentWindow?.postMessage(
            {
                source: gesttoSource,
                type: 'facilita:session',
                payload: { token: loginService.getToken(), ticketId },
            },
            embedOrigin
        );

        const releaseConversation = () => {
            setHasDraft(false);

            if (onReleasedRef.current)
                onReleasedRef.current();
        };

        const handlers = {
            'orbita:ready': sendSession,
            'orbita:loaded': () => setStatus(conversationStatuses.ready),
            'orbita:message-sent': (payload) => registerSentMessage(payload),
            'orbita:draft-changed': (payload) => setHasDraft(payload?.hasDraft === true),
            'orbita:ticket-released': releaseConversation,
            'orbita:ticket-transferred': releaseConversation,
            'orbita:session-expired': () => {
                setErrorMessage('Sua sessão expirou. Recarregue a página e tente de novo.');
                setStatus(conversationStatuses.error);
            },
            'orbita:error': (payload) => {
                setErrorMessage(payload?.message || 'Não foi possível carregar a conversa.');
                setStatus(conversationStatuses.error);
            },
        };

        const handleEmbedMessage = (event) => {
            if (event.origin !== embedOrigin)
                return;

            const data = event.data || {};

            if (data.source !== embedSource || !handlers[data.type])
                return;

            handlers[data.type](data.payload || {});
        };

        window.addEventListener('message', handleEmbedMessage);

        if (sessionAttempt)
            sendSession();

        return () => window.removeEventListener('message', handleEmbedMessage);
    }, [embedOrigin, ticketId, registerSentMessage, sessionAttempt]);

    return {
        status,
        isSlow: isSlow && status === conversationStatuses.loading,
        conversationUrl: conversation?.conversationUrl || null,
        embedSrc,
        frameRef,
        errorMessage,
        hasDraft,
        reload,
    };
};

export default useOrbitaConversation;
